import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { AddEventModal } from './AddEventModal';

export const EventsManager: React.FC = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<any>(null);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'events'), orderBy('startTime', 'asc'));
            const sn = await getDocs(q);
            setEvents(sn.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching events:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this event?')) return;
        try {
            await deleteDoc(doc(db, 'events', id));
            fetchEvents();
        } catch (err) {
            console.error("Error deleting event:", err);
            alert('Failed to delete event');
        }
    };

    const handleEdit = (event: any) => {
        setEditingEvent(event);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditingEvent(null);
        setIsModalOpen(true);
    };

    if (loading) return <div className="p-8">Loading events...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold font-serif text-secondary">Events & Itinerary</h3>
                <Button onClick={handleAdd} size="sm">Add Event</Button>
            </div>

            <div className="bg-white rounded-xl shadow-card border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 font-sans">
                    <thead className="bg-bg-subtle">
                        <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold text-secondary-light uppercase tracking-widest">Order</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-secondary-light uppercase tracking-widest">Time</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-secondary-light uppercase tracking-widest">Title (EN/ES)</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-secondary-light uppercase tracking-widest">Location</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-secondary-light uppercase tracking-widest">Event Code</th>
                            <th className="px-6 py-4 text-right text-sm font-bold text-secondary-light uppercase tracking-widest">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {events.map((e) => (
                            <tr key={e.id} className="hover:bg-bg-subtle transition-colors text-base">
                                <td className="px-6 py-4 whitespace-nowrap text-secondary-light">{e.order}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-secondary font-medium">
                                    {e.startTime?.toDate ? e.startTime.toDate().toLocaleString() : new Date(e.startTime).toLocaleString()}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-secondary font-bold">{e.title?.en}</div>
                                    <div className="text-secondary-light text-sm italic">{e.title?.es}</div>
                                </td>
                                <td className="px-6 py-4 text-secondary-light">{e.locationName}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${e.eventCode === 'IE' || e.celebration === 'IE' ? 'bg-green-50 text-green-700' : e.eventCode === 'PB' || e.celebration === 'PB' ? 'bg-blue-50 text-blue-700' : 'bg-primary-light/30 text-primary-dark'}`}>
                                        {e.eventCode || e.celebration}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                    <button onClick={() => handleEdit(e)} className="text-primary hover:underline font-bold text-sm uppercase tracking-wider">Edit</button>
                                    <button onClick={() => handleDelete(e.id)} className="text-red-500 hover:underline font-bold text-sm uppercase tracking-wider">Delete</button>
                                </td>
                            </tr>
                        ))}
                        {events.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-secondary-light italic text-base">No events found. Add your first event to get started.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AddEventModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchEvents}
                eventToEdit={editingEvent}
            />
        </div>
    );
};
